import React, { useState, useEffect, useCallback, useRef } from 'react';
import io from 'socket.io-client';

const SOCKET_SERVER = 'https://tictactoe-server-ftsf.onrender.com/';

// Server whitelist — these must match server.js ALLOWED_EMOJIS
const EMOJI_OPTIONS = [
  { emoji: '😝', label: 'Cheeky' },
  { emoji: '😂', label: 'LOL' },
  { emoji: '🔥', label: 'Fire' },
  { emoji: '🤔', label: 'Thinking' },
  { emoji: '👎', label: 'Nope' },
  { emoji: '👑', label: 'King' },
];

export default function TicTacToe() {
  // ─── Socket (created ONCE – never in deps array) ───────────────────────────
  const socketRef = useRef(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');

  // ─── Game state ────────────────────────────────────────────────────────────
  const [board, setBoard] = useState(Array(9).fill(''));
  const [gridSize, setGridSize] = useState(3);
  const [currentPlayer, setCurrentPlayer] = useState('X');
  const [gameActive, setGameActive] = useState(true);
  const [scores, setScores] = useState({ X: 0, O: 0, ties: 0 });
  const [status, setStatus] = useState("Player X's turn");

  // ─── Online state ──────────────────────────────────────────────────────────
  const [gameId, setGameId] = useState('');
  const [inputGameId, setInputGameId] = useState('');
  const playerRoleRef = useRef(null);           // ref so socket callbacks see latest value
  const [playerRole, _setPlayerRole] = useState(null);
  const setPlayerRole = (r) => { playerRoleRef.current = r; _setPlayerRole(r); };
  const [isOnlineMode, setIsOnlineMode] = useState(false);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);

  // ─── UI state ──────────────────────────────────────────────────────────────
  const [screen, setScreen] = useState('menu'); // 'menu' | 'gridSelect' | 'game'
  const [gridSelectMode, setGridSelectMode] = useState('online');
  const [isLoading, setIsLoading] = useState(false);
  const [floatingEmojis, setFloatingEmojis] = useState([]);   // [{id, emoji, side}]
  const [lastMove, setLastMove] = useState(null);
  const [winCells, setWinCells] = useState([]);
  const [showWinBanner, setShowWinBanner] = useState(false);
  const [winnerName, setWinnerName] = useState('');
  const [disconnectWarning, setDisconnectWarning] = useState(null); // e.g. 'X' or 'O'

  // ─── Win check ─────────────────────────────────────────────────────────────
  const checkWinner = useCallback((b, size) => {
    const winLength = size === 3 ? 3 : size === 6 ? 4 : 5;
    const getCell = (r, c) => b[r * size + c];
    const check = (cells) => {
      if (cells.every(v => v === 'X')) return 'X';
      if (cells.every(v => v === 'O')) return 'O';
      return null;
    };
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (c + winLength <= size) {
          const w = check(Array.from({ length: winLength }, (_, i) => getCell(r, c + i)));
          if (w) return w;
        }
        if (r + winLength <= size) {
          const w = check(Array.from({ length: winLength }, (_, i) => getCell(r + i, c)));
          if (w) return w;
        }
        if (r + winLength <= size && c + winLength <= size) {
          const w = check(Array.from({ length: winLength }, (_, i) => getCell(r + i, c + i)));
          if (w) return w;
        }
        if (r + winLength <= size && c - winLength + 1 >= 0) {
          const w = check(Array.from({ length: winLength }, (_, i) => getCell(r + i, c - i)));
          if (w) return w;
        }
      }
    }
    return null;
  }, []);

  const findWinCells = useCallback((b, size) => {
    const winLength = size === 3 ? 3 : size === 6 ? 4 : 5;
    const getIdx = (r, c) => r * size + c;
    const check = (cells, indices) => {
      const v = b[indices[0]];
      if (v && cells.every(c => c === v)) return indices;
      return null;
    };
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (c + winLength <= size) {
          const idx = Array.from({ length: winLength }, (_, i) => getIdx(r, c + i));
          const w = check(idx.map(i => b[i]), idx);
          if (w) return w;
        }
        if (r + winLength <= size) {
          const idx = Array.from({ length: winLength }, (_, i) => getIdx(r + i, c));
          const w = check(idx.map(i => b[i]), idx);
          if (w) return w;
        }
        if (r + winLength <= size && c + winLength <= size) {
          const idx = Array.from({ length: winLength }, (_, i) => getIdx(r + i, c + i));
          const w = check(idx.map(i => b[i]), idx);
          if (w) return w;
        }
        if (r + winLength <= size && c - winLength + 1 >= 0) {
          const idx = Array.from({ length: winLength }, (_, i) => getIdx(r + i, c - i));
          const w = check(idx.map(i => b[i]), idx);
          if (w) return w;
        }
      }
    }
    return [];
  }, []);

  // ─── Socket — created ONCE ─────────────────────────────────────────────────
  useEffect(() => {
    const socket = io(SOCKET_SERVER, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });
    socketRef.current = socket;

    socket.on('connect', () => setConnectionStatus('connected'));
    socket.on('disconnect', () => setConnectionStatus('disconnected'));
    socket.on('connect_error', () => setConnectionStatus('disconnected'));

    socket.on('gameState', (game) => {
      setBoard(game.board);
      setCurrentPlayer(game.currentPlayer);
      setGameActive(game.gameActive);
      setScores(game.scores);
      setGridSize(game.gridSize);
      setWaitingForOpponent(false);   // ← opponent has joined!
      setScreen('game');

      const winner = !game.gameActive ? checkWinner(game.board, game.gridSize) : null;
      if (winner) {
        setWinCells(findWinCells(game.board, game.gridSize));
        setWinnerName(`Player ${winner}`);
        setShowWinBanner(true);
        setStatus(`🎉 Player ${winner} wins!`);
        setTimeout(() => setShowWinBanner(false), 3500);
      } else if (!game.gameActive && game.board.every(c => c !== '')) {
        setWinCells([]);
        setStatus("🤝 It's a tie!");
        setShowWinBanner(true);
        setWinnerName('Tie');
        setTimeout(() => setShowWinBanner(false), 3500);
      } else {
        setWinCells([]);
        setStatus(`Player ${game.currentPlayer}'s turn`);
      }
    });

    // FIX: was 'emojiSent' — server emits 'receiveEmoji'
    socket.on('receiveEmoji', ({ emoji, role }) => {
      const isMe = role === playerRoleRef.current;
      spawnFloatingEmoji(emoji, isMe ? 'right' : 'left');
    });

    socket.on('playerDisconnected', ({ role }) => {
      setDisconnectWarning(role);
      setGameActive(false);
    });

    return () => socket.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Floating emoji system ─────────────────────────────────────────────────
  const spawnFloatingEmoji = (emoji, side) => {
    const id = Date.now() + Math.random();
    setFloatingEmojis(prev => [...prev, { id, emoji, side }]);
    setTimeout(() => setFloatingEmojis(prev => prev.filter(e => e.id !== id)), 2500);
  };

  // ─── Create game ───────────────────────────────────────────────────────────
  const createGame = (size) => {
    const socket = socketRef.current;
    if (!socket || connectionStatus !== 'connected') return;
    socket.emit('createGame', { gridSize: size }, (res) => {
      if (res?.gameId) {
        setGameId(res.gameId);
        setPlayerRole(res.role);
        setIsOnlineMode(true);
        setGridSize(size);
        setBoard(Array(size * size).fill(''));
        setCurrentPlayer('X');
        setGameActive(true);
        setScores({ X: 0, O: 0, ties: 0 });
        setWinCells([]);
        setWaitingForOpponent(true);
        setStatus("Waiting for opponent…");
        setScreen('game');
      }
    });
  };

  // ─── Join game ─────────────────────────────────────────────────────────────
  const joinGame = () => {
    const socket = socketRef.current;
    const id = inputGameId.trim().toUpperCase();
    if (!id || !socket || connectionStatus !== 'connected') return;
    setIsLoading(true);
    socket.emit('joinGame', { gameId: id }, (res) => {
      setIsLoading(false);
      if (res?.error) {
        setStatus(res.error);
      } else if (res?.success) {
        setGameId(id);
        setPlayerRole(res.role);
        setIsOnlineMode(true);
        setGridSize(res.gridSize || 3);
        setBoard(Array((res.gridSize || 3) ** 2).fill(''));
        setScores({ X: 0, O: 0, ties: 0 });
        setWinCells([]);
        setWaitingForOpponent(false);
        setStatus(`You joined! You are Player ${res.role}`);
        setScreen('game');
      }
    });
  };

  // ─── Cell click ────────────────────────────────────────────────────────────
  const handleCellClick = (index) => {
    if (board[index] !== '' || !gameActive) return;
    const socket = socketRef.current;

    if (isOnlineMode) {
      if (playerRole !== currentPlayer) return;
      if (waitingForOpponent) return;
      if (socket && connectionStatus === 'connected') {
        socket.emit('makeMove', { gameId, index });
        setLastMove(index);
      }
    } else {
      const newBoard = [...board];
      newBoard[index] = currentPlayer;
      setBoard(newBoard);
      setLastMove(index);

      const winner = checkWinner(newBoard, gridSize);
      if (winner) {
        const cells = findWinCells(newBoard, gridSize);
        setWinCells(cells);
        setScores(prev => ({ ...prev, [winner]: prev[winner] + 1 }));
        setGameActive(false);
        setStatus(`🎉 Player ${winner} wins!`);
        setWinnerName(`Player ${winner}`);
        setShowWinBanner(true);
        setTimeout(() => setShowWinBanner(false), 3500);
      } else if (newBoard.every(c => c !== '')) {
        setWinCells([]);
        setScores(prev => ({ ...prev, ties: prev.ties + 1 }));
        setGameActive(false);
        setStatus("🤝 It's a tie!");
        setWinnerName('Tie');
        setShowWinBanner(true);
        setTimeout(() => setShowWinBanner(false), 3500);
      } else {
        const next = currentPlayer === 'X' ? 'O' : 'X';
        setCurrentPlayer(next);
        setStatus(`Player ${next}'s turn`);
      }
    }
  };

  // ─── Reset / exit ──────────────────────────────────────────────────────────
  const resetGame = () => {
    setShowWinBanner(false);
    setWinCells([]);
    setLastMove(null);
    setDisconnectWarning(null);
    const socket = socketRef.current;
    if (isOnlineMode && socket && connectionStatus === 'connected') {
      socket.emit('resetGame', { gameId });
    } else {
      setBoard(Array(gridSize * gridSize).fill(''));
      setCurrentPlayer('X');
      setGameActive(true);
      setStatus("Player X's turn");
    }
  };

  const resetScores = () => {
    setShowWinBanner(false);
    setWinCells([]);
    setLastMove(null);
    const socket = socketRef.current;
    if (isOnlineMode && socket && connectionStatus === 'connected') {
      socket.emit('resetScores', { gameId });
    } else {
      setScores({ X: 0, O: 0, ties: 0 });
      setBoard(Array(gridSize * gridSize).fill(''));
      setCurrentPlayer('X');
      setGameActive(true);
      setStatus("Player X's turn");
    }
  };

  const exitToMenu = () => {
    setScreen('menu');
    setIsOnlineMode(false);
    setGameId('');
    setInputGameId('');
    setPlayerRole(null);
    playerRoleRef.current = null;
    setWaitingForOpponent(false);
    setDisconnectWarning(null);
    setBoard(Array(9).fill(''));
    setGridSize(3);
    setCurrentPlayer('X');
    setGameActive(true);
    setScores({ X: 0, O: 0, ties: 0 });
    setStatus("Player X's turn");
    setWinCells([]);
    setShowWinBanner(false);
    setLastMove(null);
  };

  const startLocalGame = (size) => {
    setIsOnlineMode(false);
    setGameId('');
    setPlayerRole(null);
    playerRoleRef.current = null;
    setGridSize(size);
    setBoard(Array(size * size).fill(''));
    setCurrentPlayer('X');
    setGameActive(true);
    setScores({ X: 0, O: 0, ties: 0 });
    setStatus("Player X's turn");
    setWinCells([]);
    setShowWinBanner(false);
    setLastMove(null);
    setScreen('game');
  };

  // ─── Send emoji ────────────────────────────────────────────────────────────
  const sendEmoji = (emoji) => {
    if (!isOnlineMode) return;
    const socket = socketRef.current;
    if (!socket || connectionStatus !== 'connected') return;
    socket.emit('sendEmoji', { gameId, emoji });
  };

  // ─── Helpers ───────────────────────────────────────────────────────────────
  const getCellTextSize = () => {
    if (gridSize === 3) return 'text-5xl';
    if (gridSize === 6) return 'text-3xl';
    return 'text-xl';
  };

  const isMyTurn = !isOnlineMode || (playerRole === currentPlayer && !waitingForOpponent);

  // ══════════════════════════════════════════════════════════════════════════
  // ─── MENU SCREEN ──────────────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════
  if (screen === 'menu') {
    return (
      <div style={styles.bg}>
        <div style={styles.noiseBg} />
        <div style={styles.blob1} />
        <div style={styles.blob2} />

        <div style={styles.card}>
          {/* Title */}
          <div style={styles.titleWrap}>
            <span style={{ ...styles.titleX }}>X</span>
            <span style={styles.titleMain}> vs </span>
            <span style={{ ...styles.titleO }}>O</span>
          </div>
          <p style={styles.subtitle}>The classic game, now with online multiplayer</p>

          {/* Connection pill */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
            <span style={{
              ...styles.pill,
              background: connectionStatus === 'connected' ? '#22c55e' : '#ef4444',
            }}>
              {connectionStatus === 'connected' ? '● Connected' : '● Connecting…'}
            </span>
          </div>

          {/* Buttons */}
          <button
            style={{ ...styles.btn, ...styles.btnBlue, opacity: connectionStatus !== 'connected' ? 0.5 : 1 }}
            disabled={connectionStatus !== 'connected'}
            onClick={() => { setGridSelectMode('online'); setScreen('gridSelect'); }}
          >
            🌐 Create Online Game
          </button>

          <div style={styles.divider}><span style={styles.dividerText}>or</span></div>

          <input
            style={styles.input}
            placeholder="Enter Game ID"
            value={inputGameId}
            maxLength={6}
            onChange={e => setInputGameId(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && joinGame()}
          />
          <button
            style={{ ...styles.btn, ...styles.btnPurple, opacity: (!inputGameId || isLoading || connectionStatus !== 'connected') ? 0.5 : 1 }}
            disabled={!inputGameId || isLoading || connectionStatus !== 'connected'}
            onClick={joinGame}
          >
            {isLoading ? '⏳ Joining…' : '🎮 Join Game'}
          </button>

          <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 20, paddingTop: 20 }}>
            <button
              style={{ ...styles.btn, ...styles.btnGray }}
              onClick={() => { setGridSelectMode('offline'); setScreen('gridSelect'); }}
            >
              🏠 Play Offline
            </button>
          </div>

          {status && !status.includes("Player X's turn") && (
            <div style={styles.errorBox}>{status}</div>
          )}
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // ─── GRID SELECT SCREEN ───────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════
  if (screen === 'gridSelect') {
    const options = [
      { size: 3, name: 'Classic', desc: '3×3 · 3 in a row', emoji: '🎯', gradient: 'linear-gradient(135deg,#10b981,#059669)' },
      { size: 6, name: 'Advanced', desc: '6×6 · 4 in a row', emoji: '⚡', gradient: 'linear-gradient(135deg,#3b82f6,#2563eb)' },
      { size: 12, name: 'Epic',    desc: '12×12 · 5 in a row', emoji: '🔥', gradient: 'linear-gradient(135deg,#8b5cf6,#7c3aed)' },
    ];
    return (
      <div style={styles.bg}>
        <div style={styles.noiseBg} />
        <div style={styles.blob1} /><div style={styles.blob2} />

        <div style={{ ...styles.card, maxWidth: 480 }}>
          <h2 style={{ textAlign: 'center', fontSize: 32, fontWeight: 900, color: '#1e293b', marginBottom: 8 }}>
            Choose Grid Size
          </h2>
          <p style={{ textAlign: 'center', color: '#64748b', marginBottom: 28, fontSize: 15 }}>
            Bigger grid = longer game = more glory
          </p>

          {options.map(({ size, name, desc, emoji, gradient }) => (
            <button
              key={size}
              style={{ ...styles.gridBtn, background: gradient }}
              onClick={() => gridSelectMode === 'offline' ? startLocalGame(size) : createGame(size)}
            >
              <span style={{ fontSize: 36 }}>{emoji}</span>
              <div style={{ flex: 1, textAlign: 'left', paddingLeft: 16 }}>
                <div style={{ fontSize: 22, fontWeight: 800 }}>{name}</div>
                <div style={{ fontSize: 13, opacity: 0.85 }}>{desc}</div>
              </div>
              <span style={{ fontSize: 28, fontWeight: 900, opacity: 0.4 }}>{size}×{size}</span>
            </button>
          ))}

          <button style={{ ...styles.btn, ...styles.btnGray, marginTop: 12 }} onClick={() => setScreen('menu')}>
            ← Back
          </button>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // ─── GAME SCREEN ──────────────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════
  const maxBoardWidth = gridSize === 12 ? 660 : gridSize === 6 ? 480 : 380;

  return (
    <div style={styles.bg}>
      <div style={styles.noiseBg} />
      <div style={styles.blob1} /><div style={styles.blob2} />

      {/* Floating emojis */}
      {floatingEmojis.map(({ id, emoji, side }) => (
        <div key={id} style={{
          position: 'fixed',
          [side === 'left' ? 'left' : 'right']: '5vw',
          bottom: '20vh',
          fontSize: 56,
          zIndex: 999,
          animation: 'floatUp 2.5s ease forwards',
          pointerEvents: 'none',
        }}>
          {emoji}
        </div>
      ))}

      {/* Win banner */}
      {showWinBanner && (
        <div style={styles.winBanner}>
          {winnerName === 'Tie' ? '🤝 It\'s a Tie!' : `🎉 ${winnerName} Wins!`}
        </div>
      )}

      <div style={{ ...styles.card, maxWidth: Math.max(maxBoardWidth + 60, 400), padding: '28px 24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <button style={styles.exitBtn} onClick={exitToMenu}>← Exit</button>
          <h1 style={{ fontSize: 26, fontWeight: 900, color: '#1e293b', margin: 0 }}>Tic-Tac-Toe</h1>
          <span style={{
            ...styles.pill,
            background: connectionStatus === 'connected' ? '#22c55e' : '#ef4444',
            fontSize: 11,
          }}>
            {connectionStatus === 'connected' ? '● Live' : '● Off'}
          </span>
        </div>

        {/* Game ID banner */}
        {isOnlineMode && gameId && (
          <div style={styles.gameIdBanner}>
            <span style={{ fontSize: 11, color: '#6366f1', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
              Game ID
            </span>
            <span style={{ fontSize: 28, fontWeight: 900, color: '#312e81', letterSpacing: 6 }}>{gameId}</span>
            <span style={{ fontSize: 12, color: '#6366f1', fontWeight: 600 }}>
              You are Player {playerRole} · {gridSize}×{gridSize}
            </span>
          </div>
        )}

        {/* Waiting overlay message */}
        {waitingForOpponent && (
          <div style={styles.waitingBox}>
            <div style={{ fontSize: 32, marginBottom: 6 }}>⏳</div>
            <div style={{ fontWeight: 800, fontSize: 16, color: '#1e293b' }}>Waiting for opponent…</div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Share the Game ID above</div>
            <div style={styles.dotLoader} className="dot-loader">
              <span /><span /><span />
            </div>
          </div>
        )}

        {/* Disconnect warning */}
        {disconnectWarning && (
          <div style={styles.disconnectBox}>
            <div style={{ fontSize: 28, marginBottom: 4 }}>🔌</div>
            <div style={{ fontWeight: 900, fontSize: 16, color: '#7f1d1d' }}>
              Player {disconnectWarning} disconnected
            </div>
            <div style={{ fontSize: 13, color: '#991b1b', marginTop: 4 }}>
              The game has ended. Start a new game or exit.
            </div>
          </div>
        )}

        {/* Scoreboard */}
        <div style={styles.scoreboard}>
          {[
            { label: 'Player X', val: scores.X, color: '#2563eb', highlight: currentPlayer === 'X' && gameActive },
            { label: 'Ties',     val: scores.ties, color: '#6366f1', highlight: false },
            { label: 'Player O', val: scores.O, color: '#7c3aed', highlight: currentPlayer === 'O' && gameActive },
          ].map(({ label, val, color, highlight }) => (
            <div key={label} style={{
              ...styles.scoreBox,
              boxShadow: highlight ? `0 0 0 3px ${color}` : 'none',
              transform: highlight ? 'scale(1.07)' : 'scale(1)',
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
              <div style={{ fontSize: 30, fontWeight: 900, color }}>{val}</div>
            </div>
          ))}
        </div>

        {/* Status bar */}
        <div style={{
          ...styles.statusBar,
          background: gameActive ? '#fef9c3' : '#dcfce7',
          borderColor: gameActive ? '#fde047' : '#86efac',
        }}>
          {status}
        </div>

        {/* Emoji bar (online only) */}
        {isOnlineMode && !waitingForOpponent && (
          <div style={styles.emojiBar}>
            {EMOJI_OPTIONS.map(({ emoji, label }) => (
              <button
                key={emoji}
                title={label}
                style={styles.emojiBtn}
                onClick={() => sendEmoji(emoji)}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}

        {/* Board */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
          gap: gridSize === 12 ? 3 : 5,
          padding: gridSize === 12 ? 10 : 14,
          background: 'linear-gradient(135deg,#2563eb,#4f46e5)',
          borderRadius: 20,
          margin: '0 auto 16px',
          maxWidth: maxBoardWidth,
          boxShadow: '0 20px 50px rgba(37,99,235,0.35)',
        }}>
          {board.map((cell, index) => {
            const isWin = winCells.includes(index);
            const isNew = index === lastMove;
            const canClick = cell === '' && gameActive && isMyTurn && !waitingForOpponent;
            return (
              <button
                key={index}
                onClick={() => handleCellClick(index)}
                disabled={!canClick}
                style={{
                  aspectRatio: '1',
                  background: isWin ? 'linear-gradient(135deg,#fbbf24,#f59e0b)' : 'rgba(255,255,255,0.95)',
                  border: 'none',
                  borderRadius: gridSize === 12 ? 6 : 10,
                  cursor: canClick ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: gridSize === 3 ? 40 : gridSize === 6 ? 26 : 16,
                  fontWeight: 900,
                  color: cell === 'X' ? '#1d4ed8' : '#7c3aed',
                  transition: 'transform 0.1s, box-shadow 0.1s',
                  transform: isWin ? 'scale(1.08)' : isNew ? 'scale(1.05)' : 'scale(1)',
                  boxShadow: isWin ? '0 4px 16px rgba(251,191,36,0.6)' : '0 2px 6px rgba(0,0,0,0.12)',
                  outline: canClick ? undefined : 'none',
                }}
                onMouseEnter={e => { if (canClick) e.currentTarget.style.transform = 'scale(1.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = isWin ? 'scale(1.08)' : 'scale(1)'; }}
              >
                {cell}
              </button>
            );
          })}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
          <button style={{ ...styles.btn, ...styles.btnBlue, flex: 1, padding: '13px 0', fontSize: 15 }} onClick={resetGame}>
            🔄 New Game
          </button>
          <button style={{ ...styles.btn, ...styles.btnPurple, flex: 1, padding: '13px 0', fontSize: 15 }} onClick={resetScores}>
            📊 Reset Scores
          </button>
        </div>
      </div>

      {/* Keyframes injected inline */}
      <style>{`
        @keyframes disconnectPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.3); }
          50% { box-shadow: 0 0 0 8px rgba(239,68,68,0); }
        }
        @keyframes floatUp {
          0%   { opacity: 1; transform: translateY(0) scale(1); }
          80%  { opacity: 0.8; transform: translateY(-120px) scale(1.3); }
          100% { opacity: 0; transform: translateY(-160px) scale(0.8); }
        }
        @keyframes winBannerIn {
          0%   { transform: translateX(-50%) translateY(-40px) scale(0.7); opacity: 0; }
          60%  { transform: translateX(-50%) translateY(8px) scale(1.05); opacity: 1; }
          100% { transform: translateX(-50%) translateY(0) scale(1); opacity: 1; }
        }
        @keyframes dotBounce {
          0%, 80%, 100% { transform: scale(0.5); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
        .dot-loader span {
          display: inline-block;
          width: 8px; height: 8px;
          background: #f59e0b;
          border-radius: 50%;
          animation: dotBounce 1.2s infinite ease-in-out;
        }
        .dot-loader span:nth-child(2) { animation-delay: 0.2s; }
        .dot-loader span:nth-child(3) { animation-delay: 0.4s; }
        .emoji-btn:hover { transform: scale(1.25) !important; box-shadow: 0 6px 20px rgba(0,0,0,0.15) !important; }
        .grid-btn:hover { transform: translateY(-2px) scale(1.02) !important; box-shadow: 0 12px 32px rgba(0,0,0,0.2) !important; }
        .action-btn:hover { transform: translateY(-2px) !important; }
      `}</style>
    </div>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────────────
const styles = {
  bg: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg,#0f172a 0%,#1e1b4b 50%,#0f172a 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 20,
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    position: 'relative',
    overflow: 'hidden',
  },
  noiseBg: {
    position: 'fixed', inset: 0,
    backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.04\'/%3E%3C/svg%3E")',
    pointerEvents: 'none', zIndex: 0,
  },
  blob1: {
    position: 'fixed', top: '-10vh', left: '-10vw',
    width: '50vw', height: '50vw', borderRadius: '50%',
    background: 'radial-gradient(circle,rgba(99,102,241,0.18),transparent 70%)',
    pointerEvents: 'none',
  },
  blob2: {
    position: 'fixed', bottom: '-10vh', right: '-10vw',
    width: '50vw', height: '50vw', borderRadius: '50%',
    background: 'radial-gradient(circle,rgba(124,58,237,0.18),transparent 70%)',
    pointerEvents: 'none',
  },
  card: {
    position: 'relative', zIndex: 1,
    background: 'rgba(255,255,255,0.97)',
    backdropFilter: 'blur(20px)',
    borderRadius: 28,
    padding: '44px 36px',
    width: '100%',
    maxWidth: 440,
    boxShadow: '0 30px 80px rgba(0,0,0,0.35)',
    border: '1.5px solid rgba(255,255,255,0.6)',
  },
  titleWrap: { textAlign: 'center', fontSize: 62, fontWeight: 900, lineHeight: 1, marginBottom: 6, letterSpacing: -2 },
  titleX: { color: '#2563eb', textShadow: '0 4px 16px rgba(37,99,235,0.3)' },
  titleO: { color: '#7c3aed', textShadow: '0 4px 16px rgba(124,58,237,0.3)' },
  titleMain: { color: '#94a3b8', fontSize: 32 },
  subtitle: { textAlign: 'center', color: '#64748b', fontSize: 14, marginBottom: 20, marginTop: 0 },
  pill: {
    display: 'inline-block',
    color: '#fff',
    padding: '5px 14px',
    borderRadius: 99,
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0.3,
  },
  btn: {
    display: 'block', width: '100%',
    border: 'none', borderRadius: 14,
    padding: '16px 20px',
    fontSize: 17, fontWeight: 800,
    cursor: 'pointer',
    marginBottom: 12,
    transition: 'transform 0.15s, box-shadow 0.15s',
    letterSpacing: 0.2,
  },
  btnBlue: { background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: '#fff', boxShadow: '0 8px 24px rgba(37,99,235,0.3)' },
  btnPurple: { background: 'linear-gradient(135deg,#8b5cf6,#7c3aed)', color: '#fff', boxShadow: '0 8px 24px rgba(124,58,237,0.3)' },
  btnGray: { background: '#f1f5f9', color: '#334155', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  divider: {
    display: 'flex', alignItems: 'center',
    margin: '4px 0 12px',
    gap: 10,
    '::before': { content: '""', flex: 1, borderTop: '1px solid #e2e8f0' },
  },
  dividerText: { color: '#94a3b8', fontSize: 13, fontWeight: 600 },
  input: {
    display: 'block', width: '100%',
    padding: '14px 18px',
    border: '2.5px solid #c7d2fe',
    borderRadius: 14,
    fontSize: 20, fontWeight: 800, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 6,
    outline: 'none', marginBottom: 12,
    boxSizing: 'border-box',
    color: '#1e293b',
  },
  errorBox: {
    marginTop: 12,
    background: '#fef2f2', color: '#ef4444',
    border: '1.5px solid #fca5a5',
    borderRadius: 12, padding: '10px 16px',
    textAlign: 'center', fontWeight: 700, fontSize: 14,
  },
  gridBtn: {
    display: 'flex', alignItems: 'center',
    width: '100%', border: 'none', borderRadius: 16,
    padding: '18px 22px', marginBottom: 12,
    color: '#fff', cursor: 'pointer',
    transition: 'transform 0.15s, box-shadow 0.15s',
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
  },
  exitBtn: {
    background: '#f1f5f9', border: 'none', borderRadius: 10,
    padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
    color: '#475569',
  },
  gameIdBanner: {
    background: 'linear-gradient(135deg,#eef2ff,#ede9fe)',
    border: '2px solid #c7d2fe',
    borderRadius: 16, padding: '12px 20px',
    textAlign: 'center', marginBottom: 14,
    display: 'flex', flexDirection: 'column', gap: 2,
  },
  waitingBox: {
    background: '#fffbeb', border: '2px solid #fde68a',
    borderRadius: 16, padding: '16px', textAlign: 'center',
    marginBottom: 14,
  },
  disconnectBox: {
    background: '#fef2f2', border: '2px solid #fca5a5',
    borderRadius: 16, padding: '16px', textAlign: 'center',
    marginBottom: 14,
    animation: 'disconnectPulse 1.5s ease-in-out infinite',
  },
  dotLoader: {
    display: 'flex', justifyContent: 'center', gap: 6, marginTop: 10,
  },
  scoreboard: {
    display: 'flex', justifyContent: 'space-around',
    background: '#f8fafc', borderRadius: 16,
    padding: '12px 8px', marginBottom: 12, gap: 8,
  },
  scoreBox: {
    flex: 1, textAlign: 'center',
    background: '#fff', borderRadius: 12,
    padding: '10px 6px',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  statusBar: {
    textAlign: 'center', fontWeight: 800, fontSize: 18,
    padding: '12px 20px', borderRadius: 14,
    border: '2.5px solid',
    marginBottom: 12,
    minHeight: 48,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    letterSpacing: 0.2,
    color: '#1e293b',
  },
  emojiBar: {
    display: 'flex', justifyContent: 'center',
    gap: 8, marginBottom: 14, flexWrap: 'wrap',
  },
  emojiBtn: {
    fontSize: 26, background: '#fff',
    border: '2px solid #e2e8f0',
    borderRadius: '50%', width: 48, height: 48,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer',
    transition: 'transform 0.15s, box-shadow 0.15s',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  winBanner: {
    position: 'fixed', top: '10vh',
    left: '50%',
    background: 'linear-gradient(135deg,#fbbf24,#f59e0b)',
    color: '#1e293b', fontWeight: 900, fontSize: 28,
    padding: '18px 40px', borderRadius: 99,
    boxShadow: '0 16px 48px rgba(251,191,36,0.5)',
    zIndex: 9999,
    animation: 'winBannerIn 0.5s cubic-bezier(.34,1.56,.64,1) forwards',
    whiteSpace: 'nowrap',
  },
};